import { Bounce, toast } from "react-toastify";
import { getAicServerUrl } from "../config";
import {
  API_CALL_ADD_CONNECTION,
  API_CALL_HOLD,
  CALL_CANNOT_DIAL,
  permissionName,
  SHOW_NOTIFICATION,
} from "../constants/constants";
import storeRTK from "../store/storeRTK";
import { ConnectInstance } from "../types/agent.type";
import { RootState } from "../store/storeConfig";
import { MakeCallOptions } from "../types/generic.types";
import { API_CALL_DIAL } from "../constants/events";
import Emitter from "../emitter";
import { isDialCallInProgress } from "../actions/call";
import { addNotificationAction } from "../connector/notifications";

/**
 * Returns a shallow clone of the object provided
 * @param {T} obj
 * @returns{T} Cloned Object
 */
export function shallowClone<T>(obj: T): T {
  return Object.assign({}, obj);
}

/**
 * Used to generate UUID's for Session Handling
 * @returns Randomly Generated UUID Session
 */
export function makeUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (a, b) {
      return (
        (b = Math.random() * 16), (a == "y" ? (b & 3) | 8 : b | 0).toString(16)
      );
    }
  );
}

export async function checkVpnConn() {
  const CURRENT_DOMAIN = await getAicServerUrl();
  return new Promise((resolve, reject) => {
    fetch(`${CURRENT_DOMAIN}/v1/ready`)
      .then((response) => response.text())
      .then(() => {
        resolve("ok");
      })
      .catch(() => {
        reject("failure");
      });
  });
}
export function checkVpnConnNTimes(
  maxRetry: number,
  callback: (error: any, status: boolean) => void,
  err?: any
) {
  if (maxRetry <= 0) {
    maxRetry = 0;
    callback(err, false);
  } else {
    if (navigator.onLine) {
      checkVpnConn()
        .then(() => callback(null, true))
        .catch((err) => {
          setTimeout(
            () => checkVpnConnNTimes(maxRetry - 1, callback, err),
            1000
          );
        });
    } else {
      setTimeout(() => checkVpnConnNTimes(maxRetry - 1, callback, err), 3000);
    }
  }
}

/**
 * A utility function to perform HTTP requests with enhanced features such as:
 * - Automatic query parameter handling
 * - Basic authentication support
 * - JSON request/response handling
 * - Custom success status validation
 * - Blob response support
 *
 * @param {Object} params - The configuration object for the request.
 * @param {string} params.url - The URL to send the request to.
 * @param {string} params.method - The HTTP method (e.g., "GET", "POST", "PUT", etc.).
 * @param {Object} [params.queryParams] - An object representing query parameters to append to the URL.
 * @param {string} [params.username] - The username for basic authentication.
 * @param {string} [params.password] - The password for basic authentication.
 * @param {number} [params.successStatus] - A specific HTTP status code to treat as a successful response.
 * @param {Object} [params.json] - The JSON payload to send in the request body (for POST, PUT, PATCH).
 * @param {boolean} [params.isJsonResponse] - Whether to force treating the response as JSON.
 * @param {string} [params.responseType] - The expected response type (e.g., "blob").
 *
 * @returns {Promise<any>} - A promise that resolves to the response data or rejects with an error.
 *
 * This function:
 * 1. Constructs headers based on the provided parameters (e.g., JSON content type, basic auth).
 * 2. Appends query parameters to the URL if provided.
 * 3. Sends the HTTP request using the Fetch API with appropriate options.
 * 4. Handles errors by parsing the response body (JSON or text) and throwing a descriptive error.
 * 5. Supports different response types (e.g., JSON, blob).
 */
export function customFetch({
  url,
  method,
  queryParams,
  username,
  password,
  successStatus,
  json,
  isJsonResponse,
  responseType,
}: any): Promise<any> {
  const headers: any = {};

  if (method === "GET") headers["Accept"] = "*/*";

  if (username && password) {
    headers["Authorization"] = `Basic ${btoa(`${username}:${password}`)}`;
  }

  if (json) {
    headers["Content-Type"] = "application/json";
  }

  if (queryParams) {
    const uSearchParams = new URLSearchParams();
    for (const key in queryParams) {
      if (queryParams[key]) uSearchParams.set(key, queryParams[key]);
    }
    url += `?${uSearchParams.toString()}`;
  }

  return fetch(url, {
    method,
    mode: "cors",
    credentials: "include",
    redirect: "follow",
    referrerPolicy: "no-referrer",
    keepalive: true,
    headers,
    body:
      method === "POST" ||
      method === "PUT" ||
      method === "PATCH" ||
      method === "DELETE"
        ? JSON.stringify(json, undefined, 2)
        : undefined,
  }).then(async (response) => {
    if ((successStatus && response.status !== successStatus) || !response.ok) {
      let errorBody: any = {};
      try {
        errorBody = await response.clone().json();
      } catch {
        try {
          errorBody = await response.text();
        } catch {
          errorBody = {};
        }
      }

      const errorMessage =
        errorBody?.error ||
        errorBody?.message ||
        (typeof errorBody === "string"
          ? errorBody
          : `HTTP error ${response.status}`);

      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).body = errorBody;

      throw error;
    }

    if (responseType === "blob") {
      return response;
    }

    const isJson =
      response.headers.get("content-type")?.includes("application/json") ||
      isJsonResponse;

    return isJson ? await response.json() : response;
  });
}

/**
 * A utility function to execute an API call with retry logic.
 *
 * @param {Function} apiFunc - The API function to be executed. It should return a Promise.
 * @param {any} apiParams - The parameters to be passed to the API function.
 * @param {number} [maxRetries=2] - The maximum number of retry attempts. Defaults to 2 if not provided.
 *
 * @returns {Promise<any>} - A Promise that resolves with the API response or rejects after all retries fail.
 *
 * This function:
 * 1. Defines retry options, including the maximum number of retries and timeout intervals between retries.
 * 2. Attempts to execute the API function.
 * 3. If the API call fails, retries the call after a specified timeout, up to the maximum number of retries.
 * 4. Resolves the Promise if the API call succeeds, or rejects it if all retries fail.
 */
export function fetchWithRetry(
  apiFunc: any,
  apiParams: any,
  maxRetries?: number
): Promise<any> {
  const retryOpts = {
    maxRetries: maxRetries || 2,
    timeouts: [1000, 1500, 3000],
  };

  return new Promise(function (resolve, reject) {
    let currentAttempt = 0;
    function runApi() {
      currentAttempt++;
      apiFunc(apiParams)
        .then((response: any) => {
          resolve(response);
        })
        .catch((err: any) => {
          if (currentAttempt < retryOpts.maxRetries) {
            setTimeout(runApi, retryOpts.timeouts[currentAttempt - 1]);
          } else {
            reject(err);
          }
        });
    }

    runApi();
  });
}

export function formatPhoneNumber(phoneNumber: string) {
  // Trim any leading and trailing whitespace
  phoneNumber = phoneNumber.trim();

  // Check if phoneNumber starts with '+'
  if (phoneNumber.startsWith("+")) {
    return phoneNumber;
  }

  // Add '+' prefix if it's not present
  return "+" + phoneNumber;
}

// Function to merge two arrays with unique elements based on a key
type AnyObject = { [key: string]: any };

// Function to merge two arrays with unique elements based on a specific key
export function mergeUnique<T extends AnyObject>(
  arr1: T[],
  arr2: T[],
  key: keyof T
): T[] {
  const unique = new Map(arr1.map((item) => [item[key], item]));

  arr2.forEach((item) => {
    if (!unique.has(item[key])) {
      unique.set(item[key], item);
    }
  });

  return Array.from(unique.values());
}

export const checkInternet = async () => {
  const CURRENT_DOMAIN = await getAicServerUrl();
  return fetch(`${CURRENT_DOMAIN}/v1/ready`)
    .then((response) => {
      if (response.ok) {
        return true;
      } else {
        // throw new Error("Server response was not ok.");
        console.log("Server response was not ok.");
      }
    })
    .catch(() => false);
};

// Rename the keys
export function renameKeys(
  obj: any,
  replaceThis: string,
  replacewith: string
): any {
  if (Array.isArray(obj)) {
    return obj.map((item) => renameKeys(item, replaceThis, replacewith));
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const newKey = key === replaceThis ? replacewith : key;
      acc[newKey] = renameKeys(obj[key], replaceThis, replacewith);
      return acc;
    }, {} as any); // Add type annotation here
  } else {
    return obj;
  }
}

export const formatTimeForCall = (callTime: number) => {
  const hours = Math.floor(callTime / 3600000)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((callTime % 3600000) / 60000)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor((callTime % 60000) / 1000)
    .toString()
    .padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

/**
 * Displays an error toast notification to the user.
 * This function creates and shows a toast message with the provided error details.
 * It is typically used to inform the user of an error that has occurred in the application.
 *
 * @param {string} msg - The error message to be displayed in the toast.
 * @param {number} id - The unique id for the toast.
 * @returns {void}
 */
export const showErrorToast = (msg: string, id: string, duration: number = 5000) => {
  return toast.error(msg, {
    toastId: id,
    position: "top-right",
    autoClose: duration,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
};

//logout all tabs
export const logoutChannel = new BroadcastChannel("logout");

/**
 * Checks if the necessary media permissions (e.g., microphone, camera) are granted.
 * This function requests the media permissions from the user and verifies if they are granted.
 *
 * @returns {Promise<boolean>}
 *          A promise that resolves to true if the permissions are granted, otherwise false.
 */
export function checkMediaPermission(callback: any, flag?: boolean) {
  navigator.permissions
    .query({ name: permissionName })
    .then(function (result) {
      if (result.state === "granted") {
        callback(true);
      } else {
        callback(false);
      }
    })
    .catch(() => {
      if (!flag) {
        setTimeout(() => checkMediaPermission(callback, true), 500);
      } else {
        callback(false);
      }
    });
}

/**
 * Retrieves the alias for the current instance.
 * This function is used to identify the active instance of the application.
 *
 * @returns {string} The alias of the current instance.
 */
export const getInstanceAlias = (): string => {
  const state = storeRTK.getState();
  const activeInstance = state.agent?.instanceConfig?.find(
    (instance: ConnectInstance) => instance.active === "true"
  );
  return activeInstance?.instance_alias;
};

export const getAmazonConnectUrlByContactId = (contactId: string): string => {
  const state = storeRTK.getState();
  const agentUrl = state?.agent?.aicConfig?.ccp_url.replace("/ccp-v2", "");
  const amazonConnectUrl = `${agentUrl}/contact-trace-records/details/${contactId}`;
  return amazonConnectUrl;
};

export const getSafeUrl = (url: string): string | null => {
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return ["http:", "https:"].includes(parsedUrl.protocol)
      ? parsedUrl.href
      : null;
  } catch {
    return null;
  }
};

export const getInstanceAliasFromStore = () => {
  const instanceAlias = storeRTK.getState().agent.instanceConfig?.[0]
    ?.instance_alias
    ? `/${storeRTK.getState().agent.instanceConfig[0].instance_alias}`
    : "";
  return instanceAlias;
};

// Format helpers
export const formatTime = (date: Date) => {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

const formatDate = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "2-digit",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
};

export const formatTimeString = (connectedTime: any) => {
  if (!connectedTime) {
    connectedTime = new Date();
  }

  const connectedDate = new Date(connectedTime);
  const now = new Date();

  // Check for invalid date
  if (isNaN(connectedDate.getTime())) {
    return "Invalid Time";
  }

  // Calculate time differences
  const diffInSeconds = Math.floor(
    (now.getTime() - connectedDate.getTime()) / 1000
  );
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  // const diffInDays = Math.floor(diffInHours / 24);

  // Get relative time display
  if (diffInSeconds < 60) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    // } else if (diffInDays === 1) {
    //   return `Yesterday ${formatTime(connectedDate)}`;
    // } else if (diffInDays < 7) {
    //   return `${diffInDays} days ago`;
  } else {
    return `${formatDate(connectedDate)}`;
  }
};

export const formatDuration = (startTime: any, endTime: any) => {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  // Check for invalid dates
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return "0s";
  }

  const durationInSeconds = Math.floor(
    (endDate.getTime() - startDate.getTime()) / 1000
  );
  const durationHours = Math.floor(durationInSeconds / 3600);
  const durationMinutes = Math.floor((durationInSeconds % 3600) / 60);
  const durationSeconds = durationInSeconds % 60;

  const durationParts = [];
  if (durationHours > 0) durationParts.push(`${durationHours}h`);
  if (durationMinutes > 0) durationParts.push(`${durationMinutes}min`);
  if (durationSeconds > 0) durationParts.push(`${durationSeconds}s`);

  return durationParts.join(" ") || "0s";
};
/**
 * Checks if a given tab is accessible based on the allowed features in the store.
 *
 * @param tabName - The name of the tab to check access for.
 * @returns A boolean indicating whether the tab is accessible.
 *
 * The function retrieves the `allowed_features` array from the Redux store.
 * It then checks if any object in the array contains the given `tabName` as a key
 * with a value of either the string `"true"` or the boolean `true`.
 */
export const tabAccessControl = (tabName: string): string => {
  const allowedFeatures = storeRTK.getState().agent?.allowed_features;
  const isAllowed = allowedFeatures?.some(
    (feature: Record<string, string | boolean>) =>
      feature[tabName] === "true" || feature[tabName] === true
  );
  return isAllowed ? "true" : "false";
};

/**
 * Formats a given label string by replacing underscores and hyphens with spaces,
 * capitalizing the first letter of each word, and converting the rest of the word to lowercase.
 *
 * @param label - The input string to format.
 * @returns A formatted string with proper capitalization and spaces, or an empty string if the input is invalid.
 *
 * Example:
 * Input: "example_label-name"
 * Output: "Example Label Name"
 */
export function formatLabel(label: string): string {
  return (
    label
      ?.replace(/[_-]/g, " ")
      .split(" ")
      .map((word) => word[0]?.toUpperCase() + word.slice(1).toLowerCase())
      .join(" ") || ""
  );
}

/**
 * Persists the history of multiple instances for a specific agent into local storage.
 * This function ensures that the history of each instance is saved and associated
 * with the correct agent, allowing for multi-instance state management.
 *
 * @param state - The current Redux state containing agent and history information.
 */
export const persistMultiInstanceHistory = (state: RootState) => {
  const agent_id = state.agent.id as string;
  const activeInstance = getInstanceAlias();

  const existingData = JSON.parse(localStorage.getItem("agent_id") || "{}");

  const storeObject = {
    ...existingData,
    [agent_id]: {
      ...existingData[agent_id],
      [activeInstance]: state.history,
    },
  };

  localStorage.setItem("agent_id", JSON.stringify(storeObject));
};

/**
 * Persists the notifications of multiple instances for a specific agent into local storage.
 * This function ensures that the notifications of each instance are saved and associated
 * with the correct agent, allowing for multi-instance state management.
 *
 * @param state - The current Redux state containing agent and notification information.
 */
export const persistMultiInstanceNotifications = (state: RootState) => {
  const agent_id = state.agent.id as string;
  const activeInstance = getInstanceAlias();

  const existingData = JSON.parse(localStorage.getItem("agent_notifications") || "{}");

  const storeObject = {
    ...existingData,
    [agent_id]: {
      ...existingData[agent_id],
      [activeInstance]: state.notifications,
    },
  };

  console.log("Persisting notifications for agent:", agent_id, "instance:", activeInstance);
  console.log("Notification state:", state.notifications);
  console.log("Storing to localStorage:", storeObject);

  localStorage.setItem("agent_notifications", JSON.stringify(storeObject));
};

const validateNumber = (number: string) => {
  const regex = new RegExp(/^(?=.{1,30}$)([+]*[0-9a-zA-Z]+[#()+ -]*)/);
  return regex.test(number);
};

/**
 * Initiates a call process by validating the number, checking network connectivity,
 * and ensuring eligibility for voice calls.
 *
 * @param number - The phone number to dial.
 * @param dialInProgress - A flag indicating if a call is already in progress.
 * @param onDialStart - Callback function to execute when dialing starts.
 * @param onDialEnd - Callback function to execute when dialing ends.
 */

export const makeCall = async ({
  number,
  dialCallInProgress,
  isAttachAttributes = false,
  callType,
}: MakeCallOptions) => {
  // if (dialCallInProgress) return;  // This code is in progress for conference will uncomment when fix
  const cleanedNumber = number.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1");
  const numberToDial =
    cleanedNumber && !cleanedNumber.startsWith("+")
      ? `+${cleanedNumber}`
      : cleanedNumber;

  if (!validateNumber(number)) {
    showErrorToast("Please enter valid number", "invalidNumber");
    return;
  }

  storeRTK.dispatch(isDialCallInProgress(true));
  if (!navigator.onLine) {
    storeRTK.dispatch(isDialCallInProgress(false));
    Emitter.emit(SHOW_NOTIFICATION, {
      message: CALL_CANNOT_DIAL,
      title: "Error",
      type: "warning",
    });
    return;
  }

  try {
    const response = storeRTK.getState().webrtc.isVoiceNetworkAvailable;
    if (response && callType === "conference") {
      // For conference call, hold current call first
      Emitter.emit(API_CALL_HOLD, { body: { held: true } });
      // Start consult leg via addConnection
      Emitter.emit(API_CALL_ADD_CONNECTION, {
        phoneNumber: numberToDial,
      });
    } else if (response && callType !== "conference") {
      Emitter.emit(API_CALL_DIAL, {
        phoneNumber: numberToDial,
        isAttachAttributes: isAttachAttributes,
      });
    } else {
      storeRTK.dispatch(isDialCallInProgress(false));
      Emitter.emit(SHOW_NOTIFICATION, {
        message: CALL_CANNOT_DIAL,
        title: "Error",
        type: "warning",
      });
    }
  } catch {
    storeRTK.dispatch(isDialCallInProgress(false));
    Emitter.emit(SHOW_NOTIFICATION, {
      message: CALL_CANNOT_DIAL,
      title: "Error",
      type: "warning",
    });
  }
};

export function b64DecodeUnicode(str: string): string {
  try {
    const binary = atob(str); // decode base64 to binary string
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0)); // convert binary string to byte array
    return new TextDecoder("utf-8").decode(bytes); // decode bytes into a full Unicode string
  } catch (e) {
    console.error("Base64 decode error:", e);
    return "NA";
  }
}

/**
 * Formats a phone number by cleaning it and ensuring it starts with a "+" if it contains digits.
 *
 * @param phoneNumber - The phone number to be formatted.
 * @returns A cleaned and properly formatted phone number.
 */
export const getFormattedPhoneNumber = (phoneNumber: string) => {
  // Clean phone number for proper matching (remove special characters except +)
  const cleanPhoneNumber = phoneNumber
    ? phoneNumber.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1")
    : "";

  // Ensure the number starts with + if it has digits
  return cleanPhoneNumber && !cleanPhoneNumber.startsWith("+")
    ? `+${cleanPhoneNumber}`
    : cleanPhoneNumber;
};

/**
 * Shows a success toast message
 * 
 * @param {string} msg - The success message to be displayed in the toast
 * @param {string} id - The unique id for the toast
 * @param {number} duration - How long to display the toast (default: 5000ms)
 * @returns {void}
 */
export const showSuccessToast = (msg: string, id: string, duration: number = 5000) => {
  return toast.success(msg, {
    toastId: id,
    position: "top-right",
    autoClose: duration,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
};

/**
 * Sends a test web notification to verify notification settings are working correctly.
 * This function checks for notification support, requests permission if needed, and displays a test notification.
 * 
 * @returns {Promise<void>} A promise that resolves when the notification is sent or fails.
 */
export const sendTestWebNotification = async (): Promise<void> => {
  // Check if notifications are supported
  if (!("Notification" in window)) {
    alert("This browser does not support notifications");
    return;
  }

  // Check if permission is granted
  if (Notification.permission === "granted") {
    try {
      // Create and show notification
      const notification = new Notification("Test Notification", {
        body: "This is a test notification to verify your notification settings are working correctly.",
        icon: "/adobeLogo.svg",
        tag: "test-notification",
        requireInteraction: true,
        silent: false
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Handle click to focus the window
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      
      console.log("Test notification sent successfully");
      
      // Show success toast to confirm notification was sent
      showSuccessToast(
        "Test notification sent! If you don't see it, check your system's Do Not Disturb or Focus mode settings.",
        "test-notification-success",
        6000
      );
    } catch (error) {
      console.error("Error sending test notification:", error);
      showErrorToast(
        "Failed to send test notification. Please check your browser settings.",
        "test-notification-error"
      );
    }
  } else if (Notification.permission === "default") {
    // Request permission first
    const permission = await Notification.requestPermission();
    
    if (permission === "granted") {
      await sendTestWebNotification(); // Retry after permission granted
    } else {
      alert("Notification permission denied. Please enable notifications in your browser settings.");
    }
  } else {
    alert("Notification permission denied. Please enable notifications in your browser settings.");
  }
};

/**
 * Creates a notification for the notification panel when permission changes
 */
export const createNotificationPermissionNotification = (permission: NotificationPermission) => {
  let title = "";
  let description = "";
  
  switch (permission) {
    case "granted":
      title = "Notifications Enabled";
      description = "Browser notifications are now enabled. You'll receive system-level alerts when contacts ring.";
      break;
    case "denied":
      title = "Notifications Disabled";
      description = "Browser notifications are disabled. You won't receive system-level alerts when contacts ring.";
      break;
    case "default":
      title = "Notification Permission Requested";
      description = "Please allow notifications to receive system-level alerts when contacts ring.";
      break;
  }
  
  const notification = {
    id: `notification-permission-${Date.now()}`,
    type: "system" as const,
    title,
    description,
    timestamp: new Date().toISOString(),
    isRead: false,
    priority: "medium" as const,
    category: "permissions"
  };
  
  const action = addNotificationAction(notification);
  storeRTK.dispatch(action);
};

/**
 * Creates a notification for the notification panel when ring device changes
 */
export const createRingDeviceNotification = (deviceName: string) => {
  const notification = {
    id: `ring-device-${Date.now()}`,
    type: "system" as const,
    title: "Ring Device Changed",
    description: `Ring sound will now play on: ${deviceName}`,
    timestamp: new Date().toISOString(),
    isRead: false,
    priority: "low" as const,
    category: "audio"
  };

  // Use the persistent notification action
  storeRTK.dispatch(addNotificationAction(notification));
};

/**
 * Attempts to query browser permissions that are closest in meaning to "sound allowed":
 * - speaker-selection (Chrome: for output device selection)
 * - autoplay (experimental/partial)
 *
 * Returns a coarse state similar to Chrome site setting: "allow" | "deny" | "automatic" | "unknown"
 * Note: Browsers do not expose per-site "Sound: Allow/Block/Automatic" to web pages.
 */
/**
 * Checks the browser's site sound/autoplay permission state.
 * 
 * IMPORTANT LIMITATION: Chrome/Edge don't expose per-site "Sound" settings via JavaScript APIs.
 * We CANNOT distinguish between "Allow" and "Automatic" settings reliably.
 * Both can result in AudioContext running after user interaction.
 * 
 * This function uses multiple detection methods:
 * 1. AudioContext state - can only reliably detect "deny" (explicitly blocked)
 * 2. Permissions API (limited support, usually unavailable)
 * 
 * @returns {Promise<"allow" | "deny" | "automatic" | "unknown">}
 *   - "allow": Rarely returned (only if Permissions API confirms it)
 *   - "deny": AudioContext blocked and cannot resume - user explicitly blocked sound
 *   - "automatic": Default state (most common) - may or may not work after idle
 *   - "unknown": Cannot determine - treat same as "automatic"
 */
export const checkSoundPermissionState = async (): Promise<
  "allow" | "deny" | "automatic" | "unknown"
> => {
  console.log("[checkSoundPermissionState] Starting...");
  // Method 1: Check AudioContext state (best effort)
  // Note: AudioContext state doesn't distinguish between "Allow" and "Automatic" settings
  // Both can result in "running" state after user interaction
  // We can only reliably detect "deny" (suspended and cannot resume)
  try {
    const AudioCtx =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AudioCtx) {
      const testCtx = new AudioCtx();
      console.log("[checkSoundPermissionState] AudioContext created, state:", testCtx.state);
      
      if (testCtx.state === "suspended") {
        console.log("[checkSoundPermissionState] AudioContext is suspended, trying to resume...");
        // Try to resume to see if it's explicitly blocked
        try {
          await testCtx.resume();
          if (testCtx.state === "running") {
            testCtx.close();
            console.log("[checkSoundPermissionState] Resumed successfully, returning 'automatic'");
            // Could resume - but we don't know if it's "Allow" or "Automatic"
            // Return "automatic" to be safe and show the dialog
            return "automatic";
          }
        } catch {
          testCtx.close();
          console.log("[checkSoundPermissionState] Resume failed, returning 'deny'");
          return "deny"; // Cannot resume - explicitly blocked
        }
        testCtx.close();
        console.log("[checkSoundPermissionState] Still suspended, returning 'deny'");
        return "deny";
      } else if (testCtx.state === "running") {
        testCtx.close();
        console.log("[checkSoundPermissionState] AudioContext is running, returning 'automatic'");
        // Context is running, but this could be "Allow" OR "Automatic" after user interaction
        // Chrome doesn't let us distinguish, so return "automatic" to show dialog
        return "automatic";
      }
      console.log("[checkSoundPermissionState] AudioContext state is neither suspended nor running:", testCtx.state);
    }
  } catch (err) {
    // AudioContext not available or failed
    console.log("[checkSoundPermissionState] AudioContext creation failed:", err);
  }

  console.log("[checkSoundPermissionState] Trying Permissions API...");
  // Method 2: Try Permissions API (limited browser support)
  if (
    "permissions" in navigator &&
    typeof (navigator as any).permissions?.query === "function"
  ) {
    const mapPermissionState = (
      state: PermissionState
    ): "allow" | "deny" | "automatic" => {
      if (state === "granted") return "allow";
      if (state === "denied") return "deny";
      return "automatic";
    };

    try {
      // Try autoplay permission (very limited support)
      const autoplay = await (navigator as any).permissions.query({
        name: "autoplay" as any,
      });
      if (autoplay && autoplay.state) {
        return mapPermissionState(autoplay.state);
      }
    } catch {
      // Not supported
    }

    try {
      // Try speaker-selection (Chrome experimental)
      const speakerSel = await (navigator as any).permissions.query({
        name: "speaker-selection" as any,
      });
      if (speakerSel && speakerSel.state) {
        return mapPermissionState(speakerSel.state);
      }
    } catch {
      // Not supported
    }
  }

  // Could not determine - show dialog to be safe
  console.log("[checkSoundPermissionState] No method worked, returning 'unknown'");
  return "unknown";
};

/**
 * Actively tests if audio playback is currently allowed by attempting to play a silent audio file.
 * This is more accurate than querying permissions because it tests the actual autoplay policy.
 * 
 * @returns {Promise<boolean>} true if audio can play, false if blocked
 */
export const canPlayAudio = async (): Promise<boolean> => {
  try {
    // Create a silent audio element
    const audio = new Audio();
    audio.volume = 0;
    audio.muted = true;
    
    // Use a data URL for a minimal silent audio file (1 second of silence)
    audio.src = "data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV";
    
    // Attempt to play
    await audio.play();
    
    // If we get here, playback was allowed
    audio.pause();
    return true;
  } catch (error: any) {
    // Check the error type
    if (error.name === "NotAllowedError" || error.name === "NotSupportedError") {
      // Explicitly blocked by autoplay policy
      return false;
    }
    // Other errors - assume blocked to be safe
    return false;
  }
};

/**
 * Plays a very short, low-volume beep using Web Audio.
 * Should be called from a user gesture (e.g., button click) so it isn't blocked by autoplay policies.
 * 
 * @param durationMs - Duration of the beep in milliseconds
 * @returns {Promise<void>}
 */
export const testSoundBeep = async (
  durationMs: number = 140
): Promise<void> => {
  try {
    const AudioCtx =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioCtx();

    // Ensure running state (on Safari/Chrome this must be in user gesture handler)
    if (ctx.state !== "running") {
      await ctx.resume();
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 880; // A5
    gainNode.gain.value = 0.05; // low volume

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start();
    await new Promise((r) => setTimeout(r, durationMs));
    oscillator.stop();

    // Give the audio graph a moment to finish
    await new Promise((r) => setTimeout(r, 30));

    // Don't close shared audio contexts; leave for reuse
  } catch (e) {
    // Swallow to avoid user disruption; caller can decide to show UI if needed
    console.warn(
      "Test beep failed (possibly blocked by autoplay or no output):",
      e
    );
    throw e; // Re-throw so caller knows it failed
  }
};

/**
 * Formats an ISO timestamp to a readable format for toast messages
 * @param isoString - ISO format timestamp
 * @returns Formatted string like "2:30 PM" or "Jan 15, 2:30 PM"
 */
export const formatTimestampForToast = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // If today, show only time
    if (diffDays === 0 && now.toDateString() === date.toDateString()) {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    
    // If older, show date and time
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return isoString; // Fallback to original if parsing fails
  }
};
